import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_workbox_3abb759f from 'nuxt_plugin_workbox_3abb759f' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_nuxticons_d9a89436 from 'nuxt_plugin_nuxticons_d9a89436' // Source: ./nuxt-icons.js (mode: 'all')
import nuxt_plugin_plugin_63984c61 from 'nuxt_plugin_plugin_63984c61' // Source: ./vuetify/plugin.js (mode: 'all')
import nuxt_plugin_sentryserver_20ed1207 from 'nuxt_plugin_sentryserver_20ed1207' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_8998bf02 from 'nuxt_plugin_sentryclient_8998bf02' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_pluginrouting_f9ffe2ba from 'nuxt_plugin_pluginrouting_f9ffe2ba' // Source: ./nuxt-i18n/plugin.routing.js (mode: 'all')
import nuxt_plugin_pluginmain_0ba517ac from 'nuxt_plugin_pluginmain_0ba517ac' // Source: ./nuxt-i18n/plugin.main.js (mode: 'all')
import nuxt_plugin_axios_f4b1757a from 'nuxt_plugin_axios_f4b1757a' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_vuewebcam_30e1c90a from 'nuxt_plugin_vuewebcam_30e1c90a' // Source: ./vue-web-cam.js (mode: 'client')
import nuxt_plugin_luxon_3432ef10 from 'nuxt_plugin_luxon_3432ef10' // Source: ../plugins/luxon (mode: 'all')
import nuxt_plugin_constants_56a5fa99 from 'nuxt_plugin_constants_56a5fa99' // Source: ../plugins/constants (mode: 'all')
import nuxt_plugin_persistence_74028169 from 'nuxt_plugin_persistence_74028169' // Source: ../plugins/persistence (mode: 'all')
import nuxt_plugin_nuxtClientInit_5086bfd4 from 'nuxt_plugin_nuxtClientInit_5086bfd4' // Source: ../plugins/nuxtClientInit (mode: 'all')
import nuxt_plugin_veevalidate_6e5ad03a from 'nuxt_plugin_veevalidate_6e5ad03a' // Source: ../plugins/vee-validate (mode: 'all')
import nuxt_plugin_signaturepad_4cb05648 from 'nuxt_plugin_signaturepad_4cb05648' // Source: ../plugins/signature-pad (mode: 'all')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error('inject(key, value) has no value provided')
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (process.client && typeof nuxt_plugin_workbox_3abb759f === 'function') {
    await nuxt_plugin_workbox_3abb759f(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxticons_d9a89436 === 'function') {
    await nuxt_plugin_nuxticons_d9a89436(app.context, inject)
  }

  if (typeof nuxt_plugin_plugin_63984c61 === 'function') {
    await nuxt_plugin_plugin_63984c61(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_20ed1207 === 'function') {
    await nuxt_plugin_sentryserver_20ed1207(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_8998bf02 === 'function') {
    await nuxt_plugin_sentryclient_8998bf02(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginrouting_f9ffe2ba === 'function') {
    await nuxt_plugin_pluginrouting_f9ffe2ba(app.context, inject)
  }

  if (typeof nuxt_plugin_pluginmain_0ba517ac === 'function') {
    await nuxt_plugin_pluginmain_0ba517ac(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_f4b1757a === 'function') {
    await nuxt_plugin_axios_f4b1757a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuewebcam_30e1c90a === 'function') {
    await nuxt_plugin_vuewebcam_30e1c90a(app.context, inject)
  }

  if (typeof nuxt_plugin_luxon_3432ef10 === 'function') {
    await nuxt_plugin_luxon_3432ef10(app.context, inject)
  }

  if (typeof nuxt_plugin_constants_56a5fa99 === 'function') {
    await nuxt_plugin_constants_56a5fa99(app.context, inject)
  }

  if (typeof nuxt_plugin_persistence_74028169 === 'function') {
    await nuxt_plugin_persistence_74028169(app.context, inject)
  }

  if (typeof nuxt_plugin_nuxtClientInit_5086bfd4 === 'function') {
    await nuxt_plugin_nuxtClientInit_5086bfd4(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_6e5ad03a === 'function') {
    await nuxt_plugin_veevalidate_6e5ad03a(app.context, inject)
  }

  if (typeof nuxt_plugin_signaturepad_4cb05648 === 'function') {
    await nuxt_plugin_signaturepad_4cb05648(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
